import { CheckCircle2, Shield, Info, ArrowRight, Clock, Star } from 'lucide-react';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function LejeEn() {
  const fordele = [
    {
      title: '100% Non-binding',
      desc: 'Rent the machine for 14 days to test it thoroughly. Absolutely no purchase pressure afterwards.'
    },
    {
      title: 'Test at your own pace',
      desc: 'Let all stable hands and riders try the machine across your daily boxes and routines.'
    },
    {
      title: 'Deducted upon purchase',
      desc: 'If you choose to purchase a new machine after the rental period, we credit the rental fee 100%!'
    }
  ];

  const vilkaar = [
    { label: 'Rental period', value: '14 calendar days' },
    { label: 'Rental price', value: '€4,225 machine / €135 rental excl. VAT' },
    { label: 'Delivery & Pick-up', value: 'Danshaker arranges delivery and pick-up' },
    { label: 'Important notice', value: 'Rental machines are strictly for testing and NOT for sale' }
  ];

  const tidslinjeTrin = [
    {
      num: '01',
      title: 'Apply for rental',
      desc: 'Fill out the form below with your contact details and desired start date.'
    },
    {
      num: '02',
      title: 'Agreement & Delivery',
      desc: 'We contact you to confirm the trial schedule, after which the machine is dispatched to your stable.'
    },
    {
      num: '03',
      title: '14-day test',
      desc: 'Use the Danshaker at your own pace on your horse boxes and experience the difference.'
    },
    {
      num: '04',
      title: 'Decision',
      desc: 'After 14 days, decide whether to return the machine or invest in a brand-new unit with rental credit.'
    }
  ];

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-950 antialiased">
      <SEO
        title="Rent a Danshaker - 14-Day Trial"
        description="Rent a Danshaker for 14 days for only €135 excl. VAT. Test the mucking and sorting machine in your own stable before deciding. Rental fee credited upon purchase."
        path="/en/rent"
        lang="en"
      />

      {/* Top Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 py-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,103,162,0.2),transparent_50%)]" />
        <div className="mx-auto max-w-7xl px-4 md:px-8 relative z-10">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/20 border border-[#0E67A2]/40 px-4 py-1.5 text-xs font-black text-sky-400 uppercase tracking-wider">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                <span>14-day trial period</span>
              </div>
              <h1 className="text-4xl font-black tracking-tight sm:text-6xl text-white">
                Rent a Danshaker
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 font-medium leading-relaxed max-w-2xl">
                Experience the machine on your own boxes before deciding. Rent a Danshaker for 14 days for just <strong className="text-white font-bold">€135 excl. VAT</strong>. If you subsequently decide to buy, the rental fee is fully deducted!
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href="#leje-formular"
                  className="rounded-xl bg-[#0E67A2] px-8 py-4 font-bold text-white shadow-lg transition hover:bg-[#0E67A2]/90 flex items-center gap-2"
                >
                  Apply for rental now
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border-2 border-slate-700 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-sm relative">
                <div className="flex items-center justify-between border-b border-slate-700 pb-4 mb-6">
                  <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Rental offer</span>
                  <div className="flex items-center gap-1 text-amber-400">
                    <Star className="h-4 w-4 fill-amber-400" />
                    <Star className="h-4 w-4 fill-amber-400" />
                    <Star className="h-4 w-4 fill-amber-400" />
                    <Star className="h-4 w-4 fill-amber-400" />
                    <Star className="h-4 w-4 fill-amber-400" />
                  </div>
                </div>
                <div className="text-4xl sm:text-5xl font-black text-white mb-2">
                  €135
                </div>
                <p className="text-xs text-slate-400 font-semibold mb-6">excl. VAT for a full 14 days</p>

                <ul className="space-y-3.5 text-sm font-medium text-slate-200">
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0" />
                    <span>14 full calendar days in your own stable</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0" />
                    <span>Fee is 100% credited if you buy</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0" />
                    <span>Thorough phone guidance from Rasmus</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0" />
                    <span>No purchase obligation whatsoever</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Key Advantages */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-8 md:grid-cols-3">
            {fordele.map((f, i) => (
              <div key={i} className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-8 shadow-sm">
                <div className="h-12 w-12 rounded-xl bg-[#0E67A2]/10 text-[#0E67A2] flex items-center justify-center font-black text-xl mb-6">
                  0{i + 1}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{f.title}</h3>
                <p className="text-sm text-slate-700 font-medium leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works - Timeline */}
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">How the rental trial works</h2>
            <p className="mt-4 text-slate-700 font-semibold">From inquiry to trial in 4 straightforward steps</p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {tidslinjeTrin.map((st, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm relative">
                <span className="text-xs font-black text-[#0E67A2] tracking-widest block mb-2">{st.num}</span>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{st.title}</h3>
                <p className="text-xs text-slate-600 font-medium leading-relaxed">{st.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="leje-formular" className="py-24 bg-white scroll-mt-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="text-center space-y-4 mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
              <Shield className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Non-binding reservation</span>
            </div>
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">
              Apply to rent a Danshaker
            </h2>
            <p className="text-slate-600 text-base max-w-xl mx-auto">
              Send us your details below. We will contact you promptly to arrange delivery and confirm dates.
            </p>
          </div>

          <div className="rounded-3xl border-2 border-slate-200 bg-slate-50 p-6 md:p-10 shadow-xl">
            <ContactForm 
              source="Leje af Danshaker (14 dage)" 
              defaultService="Leje af Danshaker (14 dage)"
              buttonText="Send rental request"
              lang="en"
            />
          </div>

          {/* Terms info */}
          <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6 space-y-4">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Info className="h-4 w-4 text-[#0E67A2]" />
              Terms & Rental Details
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 text-xs text-slate-700 font-medium">
              {vilkaar.map((v, i) => (
                <div key={i} className="border-b border-slate-200 pb-2">
                  <span className="text-slate-500 font-bold block">{v.label}</span>
                  <span className="text-slate-900 font-semibold">{v.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
